/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ScatterChartProps, ScatterItem } from './scatterChart.types';
import { findNearestDataPoint } from '#utils/PRIVATE_chartUtils/layoutUtils';
import { getMarkers, getSeriesData } from '#utils/PRIVATE_chartUtils/utils';
import { MarkersWithLabel } from '#PRIVATE_Chart/MarkersWithLabel';
import { getLineAreaChartNavUtil } from '#utils/PRIVATE_chartUtils/navUtils';
import { Chart } from '#PRIVATE_Chart';
import { Scale, TimeAxisProps, SizeHandle } from '#utils/UNSAFE_visTypes/chart';
import { useUser } from '#hooks/UNSAFE_useUser';
import { getColorRamp } from '#utils/UNSAFE_visUtils';
import { Point } from '#utils/PRIVATE_visSVGUtils';
import { getLineAreaSegments } from '#utils/PRIVATE_chartUtils/utils';
import { getItemColor, getMarkerType } from '#utils/PRIVATE_chartUtils/dataStyleUtils';
import { LineSeries } from '#PRIVATE_LineAreaComponents/LineSeries';
import { forwardRef, Ref } from 'preact/compat';
import { VNode } from 'preact';

export type ForwardRefScatterChart = <K extends string | number, D extends ScatterItem<K>>(
  props: ScatterChartProps<K, D> & {
    ref?: Ref<SizeHandle>;
  }
) => VNode | null;

/**
 * Scatter charts define data items using x and y values, making them useful for viewing relationships between 2 dimensions.
 */
export const ScatterChart = forwardRef(
  <K extends string | number, D extends ScatterItem<K>>(
    {
      width,
      height,
      selectedIds = [],
      hiddenIds = [],
      highlightedIds,
      hideAndShowBehavior = 'none',
      orientation = 'vertical',
      yAxis,
      series,
      groups,
      getDataItem,
      selectionMode = 'none',
      drilling = 'off',
      dataLabelEffect = 'outline',
      ...props
    }: ScatterChartProps<K, D>,
    ref: Ref<SizeHandle>
  ) => {
    const { direction, forcedColors } = useUser();
    const isHighContrast = forcedColors === 'active';
    const isTestEnv = props.testId !== undefined;
    const isRtl = direction === 'rtl';
    const isLog = yAxis?.scale === 'log';
    const timeAxisType = (props.xAxis as TimeAxisProps)?.timeAxisType;
    const hiddenSet = new Set(hiddenIds);
    const selectedSet = new Set(selectedIds);
    const highlightedSet = new Set(highlightedIds);
    const isHoriz = orientation === 'horizontal';
    const colors = getColorRamp();
    const renderedSeries = getSeriesData<K, D>(
      series?.length,
      groups?.length,
      getDataItem,
      hiddenSet,
      isLog
    );

    const renderedGetDataItem = (seriesIndex: number, groupIndex: number): D | undefined =>
      getDataItem(seriesIndex, groupIndex);

    const getGapRatio = () => {
      return 1;
    };

    const getNextChartItemFunc = (startIndex: number, endIndex: number) => {
      // TODO: JET-71301
      return getLineAreaChartNavUtil<D>(
        getDataItem,
        series.length,
        startIndex,
        endIndex,
        false,
        isRtl,
        isHoriz
      );
    };

    const getDataItemPos = (xScale: Scale, yScale: Scale) => {
      return (seriesIndex: number, groupIndex: number) => {
        // Scatter shouldn't create renderedSeries. Can filter for hidden item here.
        // TODO: JET-71332
        const item = renderedSeries[seriesIndex][groupIndex] as { x: number; y: number };
        if (item?.x == null && item?.y) {
          return;
        }
        const x = xScale.transform(item?.x!);
        const y = yScale.transform(item?.y!);
        return { x, y, height: 0, width: 0 };
      };
    };

    const getMarkerColorAndType = (seriesIndex: number, groupIndex: number) => {
      return {
        color: getItemColor(seriesIndex, groupIndex, series, getDataItem),
        type: getMarkerType(seriesIndex, groupIndex, getDataItem)
      };
    };

    const findNearest = (xScale: Scale, yScale: Scale) => {
      const getSeriesDataInfo = () => {
        return {
          offset: 0,
          dataWidth: 0
        };
      };

      const getYCoord = (value: number, yScale: Scale) => {
        return yScale.transform(value);
      };

      return findNearestDataPoint<K, D>(
        series,
        groups,
        xScale,
        yScale,
        renderedGetDataItem,
        false,
        getSeriesDataInfo,
        getYCoord,
        isLog,
        0,
        hiddenSet,
        timeAxisType
      );
    };
    const markers = getMarkers();
    const getOffset = () => {
      return { startOffset: 0, endOffset: 0 };
    };
    return (
      <Chart
        ref={ref}
        width={width}
        height={height}
        selectedIds={selectedSet}
        hiddenIds={hiddenIds}
        hideAndShowBehavior={hideAndShowBehavior}
        orientation={orientation}
        yAxis={yAxis}
        series={series}
        groups={groups}
        overview="off"
        defaultOverviewContentRenderer={() => null}
        selectionMode={selectionMode}
        drilling={drilling}
        getDataItem={getDataItem}
        findNearest={findNearest}
        getDataItemPos={getDataItemPos}
        getMarkerInfo={getMarkerColorAndType}
        renderGridLinesInFront={false}
        isDataXAxis={true}
        getNavUtil={getNextChartItemFunc}
        getGapRatio={getGapRatio}
        getStartAndEndAxesOffset={getOffset}
        isRtl={isRtl}
        {...props}>
        {({
          xStartIndex,
          xEndIndex,
          xScale,
          yScale,
          activeId,
          focusedItemInfo,
          hoveredItemInfo,
          isPointInsideMarquee,
          defaultFontSize,
          getTextDimensions,
          axisStepWidth,
          getItemAriaLabel
        }) => {
          return (
            <>
              {renderedSeries.map((lineAreaSeries, index) => {
                if (lineAreaSeries.length === 0) {
                  /* TODO: need a better way to handle when a series is hidden. */
                  return;
                }
                const seriesItem = series[index];
                const isLineRendered = seriesItem.lineType && seriesItem.lineType !== 'none';
                const lineSegmentCoords: Point[][] = getLineAreaSegments<K, D>(
                  yScale,
                  xScale,
                  lineAreaSeries,
                  xStartIndex,
                  xEndIndex,
                  isLog,
                  groups,
                  index,
                  renderedGetDataItem,
                  timeAxisType,
                  true
                );

                const isHighlighted =
                  highlightedIds === undefined ||
                  highlightedIds.length === 0 ||
                  highlightedSet.has(seriesItem.id);

                return (
                  <>
                    {isLineRendered && (
                      <LineSeries
                        lineType={seriesItem.lineType}
                        /* TODO: confirm with UX if this is contrastLine (changes depending on dark/light mode) or always white. 
                        It's always white in legacy.*/
                        lineColor={seriesItem.lineColor}
                        /* The area generated in legacy are bit apart and not touching when stacked. Preact areas are a bit closer than legacy when curved. 
                      Increased the contrast linewidth (to 2.5 from legacy 1.25) to give legacy visual treatment.
                      TODO: investivage why legacy areas are not touching. maybe we want to do the same in preact svg utils that
                      generates this curve.
                     */
                        lineWidth={seriesItem.lineWidth}
                        lineStyle={seriesItem.lineStyle}
                        color={seriesItem.lineColor || colors[index % colors.length]}
                        lineSegmentCoords={lineSegmentCoords}
                        isHorizontal={isHoriz}
                        isHighlighted={isHighlighted}
                        seriesIndex={index}
                        groupWidth={axisStepWidth}
                        isRtl={isRtl}
                        timeAxisType={timeAxisType}
                      />
                    )}
                    <MarkersWithLabel
                      yScale={yScale}
                      xScale={xScale}
                      groups={groups}
                      series={seriesItem}
                      timeAxisType={timeAxisType}
                      color={
                        seriesItem.lineColor ||
                        colors[
                          index % colors.length
                        ] /* TODO : make this use getItemColor function from dataStyleUtils. */
                      }
                      seriesIndex={index}
                      startIndex={xStartIndex}
                      endIndex={xEndIndex}
                      markerType={markers[index % markers.length]}
                      orientation={orientation}
                      isLog={isLog}
                      activeId={activeId}
                      getDataItem={getDataItem}
                      isPointInsideMarquee={isPointInsideMarquee}
                      selectedIds={selectedSet}
                      highlightedIds={highlightedIds !== undefined ? highlightedSet : undefined}
                      focusedItemIndex={
                        focusedItemInfo.seriesIndex === index &&
                        focusedItemInfo.isFocusVisible &&
                        !(props.isDataCursorEnabled && props.dataCursorStyle?.isMarkerDisplayed)
                          ? focusedItemInfo.groupIndex
                          : undefined
                      }
                      hoveredItemIndex={
                        hoveredItemInfo?.isCurrent && hoveredItemInfo.seriesIndex === index
                          ? hoveredItemInfo.groupIndex
                          : undefined
                      }
                      isSelectionEnabled={
                        selectionMode === 'single' || selectionMode === 'multiple'
                      }
                      isDrillEnabled={drilling === 'on'}
                      defaultFontSize={defaultFontSize}
                      dataLabelEffect={dataLabelEffect}
                      isHighContrast={isHighContrast}
                      isTestEnv={isTestEnv}
                      isDataXAxis={true}
                      getItemAriaLabel={getItemAriaLabel}
                      getTextDimensions={getTextDimensions}
                      isRtl={isRtl}
                    />
                  </>
                );
              })}
            </>
          );
        }}
      </Chart>
    );
  }
) as ForwardRefScatterChart;
